/**
 * Creative Production — leadership performance tracking for the Video Editing
 * and Script Writing teams.
 *
 * Data source: the existing Work Update tables, read through the
 * SECURITY DEFINER RPC `get_creative_production_report`, which returns only
 * aggregated (department, user_id, submission_date, output_count) rows for the
 * departments the caller is authorised to see.
 *
 *  - Videos  : editor_video_submissions   (1 row = 1 video),  deleted_at IS NULL
 *  - Scripts : scriptwriter_submissions   (1 row = 1 script), deleted_at IS NULL
 *  - Date column for both: submission_date (IST date, compared as a date)
 */
import {
  addDays,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { nowIst } from "@/lib/tz";
import type { AppRole, Profile } from "@/lib/auth-context";
import type { ModeratorPermissions } from "@/lib/moderator-permissions";

export type CreativeDept = "editor" | "scriptwriter";
export const CREATIVE_DEPTS: CreativeDept[] = ["editor", "scriptwriter"];
export const CREATIVE_DEPT_LABELS: Record<CreativeDept, string> = {
  editor: "Video Editing",
  scriptwriter: "Script Writing",
};

export type RangeKey = "today" | "week" | "month";
export const RANGE_LABELS: Record<RangeKey, string> = {
  today: "Today",
  week: "This Week",
  month: "This Month",
};

export interface ReportRow {
  department: CreativeDept;
  user_id: string;
  submission_date: string;
  output_count: number;
}

const iso = (d: Date) => format(d, "yyyy-MM-dd");

/** IST week runs Monday → Sunday. */
export function weekStart(d: Date = nowIst()): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export interface Period {
  from: string;
  to: string;
}

export function periodFor(range: RangeKey): Period {
  const now = nowIst();
  if (range === "today") return { from: iso(now), to: iso(now) };
  if (range === "week") {
    const s = weekStart(now);
    return { from: iso(s), to: iso(addDays(s, 6)) };
  }
  return { from: iso(startOfMonth(now)), to: iso(endOfMonth(now)) };
}

export function currentWeek(): Period {
  const s = weekStart();
  return { from: iso(s), to: iso(addDays(s, 6)) };
}

export function previousWeek(): Period {
  const s = weekStart(subWeeks(nowIst(), 1));
  return { from: iso(s), to: iso(addDays(s, 6)) };
}

export function currentMonth(): Period {
  const now = nowIst();
  return { from: iso(startOfMonth(now)), to: iso(endOfMonth(now)) };
}

/**
 * Widest window needed by the page: previous week start … latest end of
 * (selected range, current week, current month).
 */
export function fetchWindow(range: RangeKey): Period {
  return fetchWindowFor(periodFor(range));
}

/** Same, for an arbitrary selected period (presets or custom range). */
export function fetchWindowFor(sel: Period): Period {
  const froms = [sel.from, previousWeek().from, currentWeek().from, currentMonth().from];
  const tos = [sel.to, currentWeek().to, currentMonth().to];
  return {
    from: froms.sort()[0],
    to: tos.sort()[tos.length - 1],
  };
}

/** A date-range selection: one of the presets, or an explicit custom range. */
export type SelectionKey = RangeKey | "custom";
export interface Selection {
  key: SelectionKey;
  from: string;
  to: string;
}

export function selectionFromPreset(key: RangeKey): Selection {
  const p = periodFor(key);
  return { key, from: p.from, to: p.to };
}

export function periodOf(sel: Selection): Period {
  return sel.key === "custom" ? { from: sel.from, to: sel.to } : periodFor(sel.key);
}


// ---------- access ----------

/** Departments this viewer may see (mirror of public.creative_production_depts). */
export function allowedCreativeDepts(
  role: AppRole | null,
  profile: Profile | null,
): CreativeDept[] {
  if (!profile || profile.status !== "active") return [];
  if (role === "admin") return [...CREATIVE_DEPTS];
  if (profile.department === "hr") return [...CREATIVE_DEPTS];

  const out: CreativeDept[] = [];
  // Team lead of a creative department always sees that department.
  if (
    profile.is_team_lead &&
    (profile.department === "editor" || profile.department === "scriptwriter")
  ) {
    out.push(profile.department);
  }
  // Moderator permissions are additive on top of any team-lead department.
  if (role === "moderator") {
    const perms = (profile.moderator_permissions ?? {}) as ModeratorPermissions;
    if (perms.section_creative_production !== false) {
      if (perms.departments?.editor?.view && !out.includes("editor")) out.push("editor");
      if (perms.departments?.scriptwriter?.view && !out.includes("scriptwriter"))
        out.push("scriptwriter");
    }
  }
  return out;
}

export function canManageGoals(role: AppRole | null, profile: Profile | null): boolean {
  return role === "admin" || profile?.department === "hr";
}

// ---------- fetching ----------

export async function fetchCreativeReport(period: Period): Promise<ReportRow[]> {
  const rows: ReportRow[] = [];
  const pageSize = 1000;
  let offset = 0;
  // Paginate to completion — aggregated rows can exceed the PostgREST cap.
  for (;;) {
    const { data, error } = await (supabase as never as {
      rpc: (fn: string, args: unknown) => {
        range: (a: number, b: number) => Promise<{ data: ReportRow[] | null; error: unknown }>;
      };
    })
      .rpc("get_creative_production_report", { from_date: period.from, to_date: period.to })
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    const page = (data ?? []) as ReportRow[];
    rows.push(...page);
    if (page.length < pageSize) break;
    offset += pageSize;
  }
  return rows;
}

export interface DirectoryPerson {
  id: string;
  full_name: string;
  avatar_url: string | null;
  department: string;
  status: string;
}

export async function fetchCreativeDirectory(
  depts: CreativeDept[],
): Promise<DirectoryPerson[]> {
  if (depts.length === 0) return [];
  const { data, error } = await supabase
    .from("profiles_directory")
    .select("id, full_name, avatar_url, department, status")
    .in("department", depts);
  if (error) throw error;
  return (data ?? []) as DirectoryPerson[];
}

export interface ProductionMember extends DirectoryPerson {
  is_team_lead: boolean;
  role: string;
}

/**
 * Members of the creative departments with their leadership flags, via the
 * SECURITY DEFINER RPC `get_creative_production_members` (scoped to the
 * departments the caller may see).
 */
export async function fetchCreativeMembers(): Promise<ProductionMember[]> {
  const { data, error } = await (supabase as never as {
    rpc: (fn: string) => Promise<{ data: ProductionMember[] | null; error: unknown }>;
  }).rpc("get_creative_production_members");
  if (error) throw error;
  return (data ?? []) as ProductionMember[];
}

/**
 * Production employees only — leadership accounts (team leads, admins,
 * moderators) are excluded from leaderboards and member counts.
 */
export function isProductionEmployee(m: ProductionMember, dept: CreativeDept): boolean {
  return (
    m.department === dept &&
    m.status === "active" &&
    !m.is_team_lead &&
    m.role === "user"
  );
}

// ---------- goals ----------

export interface DepartmentGoal {
  id: string;
  department: CreativeDept;
  period_type: "weekly" | "monthly";
  target_value: number;
  effective_from: string;
}


export async function fetchDepartmentGoals(): Promise<DepartmentGoal[]> {
  const { data, error } = await supabase
    .from("department_goals")
    .select("id, department, period_type, target_value, effective_from")
    .order("effective_from", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DepartmentGoal[];
}

/** Latest goal whose effective_from is on/before the period start. */
export function resolveGoal(
  goals: DepartmentGoal[],
  dept: CreativeDept,
  periodType: "weekly" | "monthly",
  periodStart: string,
): number | null {
  const match = goals
    .filter(
      (g) =>
        g.department === dept &&
        g.period_type === periodType &&
        g.effective_from <= periodStart,
    )
    .sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0];
  return match ? match.target_value : null;
}

export async function upsertDepartmentGoal(input: {
  department: CreativeDept;
  period_type: "weekly" | "monthly";
  target_value: number;
  effective_from: string;
  updated_by: string;
}) {
  const { error } = await supabase
    .from("department_goals")
    .upsert(input as never, { onConflict: "department,period_type,effective_from" });
  if (error) throw error;
}

// ---------- aggregation ----------

export function inRange(date: string, p: Period) {
  return date >= p.from && date <= p.to;
}

export function totalFor(rows: ReportRow[], dept: CreativeDept, p: Period): number {
  return rows.reduce(
    (s, r) => (r.department === dept && inRange(r.submission_date, p) ? s + r.output_count : s),
    0,
  );
}

export function perUser(
  rows: ReportRow[],
  dept: CreativeDept,
  p: Period,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    if (r.department !== dept || !inRange(r.submission_date, p)) continue;
    m.set(r.user_id, (m.get(r.user_id) ?? 0) + r.output_count);
  }
  return m;
}

/** Distinct contributors with at least one counted row in the range. */
export function contributorCount(
  rows: ReportRow[],
  dept: CreativeDept,
  p: Period,
  allowedIds?: Set<string>,
): number {
  const s = new Set<string>();
  for (const r of rows) {
    if (r.department === dept && inRange(r.submission_date, p) && r.output_count > 0) {
      if (allowedIds && !allowedIds.has(r.user_id)) continue;
      s.add(r.user_id);
    }
  }
  return s.size;
}

export function pctChange(current: number, previous: number): { label: string; dir: 1 | -1 | 0 } {
  if (previous === 0 && current === 0) return { label: "0%", dir: 0 };
  if (previous === 0) return { label: "New", dir: 1 };
  const pct = ((current - previous) / previous) * 100;
  return {
    label: `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`,
    dir: pct > 0 ? 1 : pct < 0 ? -1 : 0,
  };
}

/** Day list for a period, inclusive. */
export function daysOf(p: Period): string[] {
  const out: string[] = [];
  let d = parseISO(p.from);
  const end = parseISO(p.to);
  while (d <= end) {
    out.push(iso(d));
    d = addDays(d, 1);
  }
  return out;
}
